import React, { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import {
    BackOfficeTrailBasicInfoResp,
    BackOfficeTrailRolePrivilegeResp,
    BackOfficeTrailActivityDetailResp,
} from 'domain/payload/response/backOfficeTrails/BackOfficeTrailsResp';
import { DateRangeValue } from 'component/DateRangeFilterDropdown';
import BackOfficeBasicInfoCard from '../../component/userDetailModal/components/BackOfficeBasicInfoCard';
import BackOfficeRolePrivilegesCard from '../../component/userDetailModal/components/BackOfficeRolePrivilegesCard';
import BackOfficeActivityLogCard from '../../component/userDetailModal/components/BackOfficeActivityLogCard';
import UserService from 'service/UserService';

interface BackOfficeUserDetailModalProps {
    show: boolean;
    onClose: () => void;
    userId?: string | null;
    token?: string | null;
}

const UserTrailModal: React.FC<BackOfficeUserDetailModalProps> = ({
    show,
    onClose,
    userId,
    token: propToken,
}) => {
    const { profile } = useSelector((state: any) => state.auth);
    const token = propToken || profile?.data?.token;

    // Detail States
    const [basicInfo, setBasicInfo] = useState<BackOfficeTrailBasicInfoResp | null>(null);
    const [rolePrivilege, setRolePrivilege] = useState<BackOfficeTrailRolePrivilegeResp | null>(null);
    const [detailLoading, setDetailLoading] = useState(false);

    // Activity Log States
    const [activities, setActivities] = useState<BackOfficeTrailActivityDetailResp[]>([]);
    const [activityLoading, setActivityLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(5);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);

    // Filters
    const [dateRange, setDateRange] = useState<DateRangeValue>({ startDate: null, endDate: null });
    const [activityType, setActivityType] = useState<string>('');
    const [searchTerm, setSearchTerm] = useState<string>('');

    const { userTrailDetail, userTrailActivityList } = UserService();

    // Fetch Basic Info & Role Privileges Detail
    const fetchDetail = useCallback(async () => {
        if (!token || !userId) return;
        setDetailLoading(true);
        try {
            const resp = await userTrailDetail(token, userId);
            if (resp.data) {
                setBasicInfo(resp.data.basicInfo || null);
                setRolePrivilege(resp.data.rolePrivilege || null);
            }
        } catch (err: any) {
            console.error('Error fetching back office trail detail:', err);
        } finally {
            setDetailLoading(false);
        }
    }, [token, userId]);

    // Fetch Activity Log
    const fetchActivities = useCallback(
        async (
            page: number = currentPage,
            size: number = pageSize,
            search: string = searchTerm,
            range: DateRangeValue = dateRange,
            type: string = activityType
        ) => {
            if (!token || !userId) return;
            setActivityLoading(true);
            try {
                const resp = await userTrailActivityList(
                    token,
                    page,
                    size,
                    search || null,
                    range.startDate || null,
                    range.endDate || null,
                    type || null,
                    userId || null
                );

                setActivities(resp.data || []);
                setTotalPages(resp.pagination?.totalPage || 1);
                setTotalRecords(resp.pagination?.totalRecords || (resp.data ? resp.data.length : 0));
            } catch (err: any) {
                console.error('Error fetching activities:', err);
                setActivities([]);
            } finally {
                setActivityLoading(false);
            }
        },
        [token, userId, currentPage, pageSize, searchTerm, dateRange, activityType]
    );

    useEffect(() => {
        if (show && userId && token) {
            fetchDetail();
            fetchActivities(1, pageSize, searchTerm, dateRange, activityType);
        }
    }, [show, userId, token]);

    useEffect(() => {
        if (!show) {
            setBasicInfo(null);
            setRolePrivilege(null);
            setActivities([]);
            setCurrentPage(1);
            setPageSize(5);
            setDateRange({ startDate: null, endDate: null });
            setActivityType('');
            setSearchTerm('');
        }
    }, [show]);

    const handleSearchSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setCurrentPage(1);
        fetchActivities(1, pageSize, searchTerm, dateRange, activityType);
    };

    const handleDateRangeChange = (val: DateRangeValue) => {
        setDateRange(val);
        setCurrentPage(1);
        fetchActivities(1, pageSize, searchTerm, val, activityType);
    };

    const handleActivityTypeChange = (type: string) => {
        setActivityType(type);
        setCurrentPage(1);
        fetchActivities(1, pageSize, searchTerm, dateRange, type);
    };

    const handlePageSizeChange = (size: number) => {
        setPageSize(size);
        setCurrentPage(1);
        fetchActivities(1, size, searchTerm, dateRange, activityType);
    };

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
        fetchActivities(page, pageSize, searchTerm, dateRange, activityType);
    };

    if (!show) return null;

    return (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-xl modal-dialog-scrollable">
                <div className="modal-content position-relative">
                    <div className="modal-header">
                        <h5 className="modal-title fw-bold">Back Office User Detail</h5>
                        <button
                            type="button"
                            className="btn-close"
                            onClick={onClose}
                            aria-label="Close"
                        ></button>
                    </div>

                    <div className="modal-body p-4 bg-light">
                        {/* Top Row: Basic Info & Active Roles & Privileges */}
                        <div className="row g-4 mb-4">
                            <div className="col-12 col-lg-6">
                                <BackOfficeBasicInfoCard
                                    basicInfo={basicInfo}
                                    loading={detailLoading}
                                />
                            </div>
                            <div className="col-12 col-lg-6">
                                <BackOfficeRolePrivilegesCard
                                    rolePrivilege={rolePrivilege}
                                    loading={detailLoading}
                                />
                            </div>
                        </div>

                        {/* Bottom Row: Activity Log */}
                        <div className="row">
                            <div className="col-12">
                                <BackOfficeActivityLogCard
                                    activities={activities}
                                    loading={activityLoading}
                                    totalRecords={totalRecords}
                                    totalPages={totalPages}
                                    currentPage={currentPage}
                                    pageSize={pageSize}
                                    onPageChange={handlePageChange}
                                    onPageSizeChange={handlePageSizeChange}
                                    onDateRangeChange={handleDateRangeChange}
                                    activityType={activityType}
                                    onActivityTypeChange={handleActivityTypeChange}
                                    searchTerm={searchTerm}
                                    onSearchChange={setSearchTerm}
                                    onSearchSubmit={handleSearchSubmit}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="modal-footer border-top">
                        <button
                            type="button"
                            className="btn btn-outline-secondary px-4"
                            onClick={onClose}
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UserTrailModal;
